import React, { useRef, useState } from 'react'
import { Button, Popover, PopoverBody } from 'reactstrap'
import { DateRange } from 'react-date-range'
import moment from 'moment'

export const formatDateLabel = (date) => moment(date).format('MMM D, YYYY')

/**
 * The date-range button + popover, extracted out of ReportToolbar so a
 * second caller (the Investigate query bar, `InvestigateTab.js`) can host its
 * own instance without duplicating the reactstrap Popover / react-date-range
 * wiring.
 *
 * `id` has to be unique per mounted instance -- reactstrap's `Popover`
 * targets its trigger by DOM id (`target={id}`), so two instances sharing the
 * default would either fight over the same id or have the second popover
 * attach to the first instance's button. ReportToolbar keeps the original
 * `reportsDateRange` id (its own default callers don't need to think about);
 * the Investigate bar passes its own `investigateDateRange`.
 */
const DateRangeControl = ({ id = 'reportsDateRange', startDate, endDate, onDateChange, className }) => {
    const [isOpen, setIsOpen] = useState(false)
    const toggleRef = useRef(null)

    // Focus returns to the trigger on every close path (click on the
    // trigger itself, click-outside, or Escape) -- reactstrap's Popover
    // (trigger="legacy") funnels all three through this one `toggle` prop,
    // so routing them all through `close` here is what keeps focus from
    // being stranded on document.body no matter which path closed it.
    const close = () => {
        setIsOpen(false)
        toggleRef.current?.focus()
    }

    const toggle = () => {
        if (isOpen) {
            close()
        } else {
            setIsOpen(true)
        }
    }

    return (
        <>
            <Button
                id={id}
                innerRef={toggleRef}
                className={`reports-date-button${className ? ` ${className}` : ''}`}
                onClick={toggle}
            >
                <i className="fa fa-calendar" aria-hidden="true"></i>
                {formatDateLabel(startDate)} - {formatDateLabel(endDate)}
            </Button>
            <Popover trigger="legacy" placement="bottom" isOpen={isOpen} target={id} toggle={toggle}>
                <PopoverBody className="reports-date-popover">
                    <DateRange
                        ranges={[{ startDate: new Date(startDate), endDate: new Date(endDate), key: 'selection' }]}
                        onChange={(ranges) => onDateChange(ranges.selection)}
                        editableDateInputs={true}
                        moveRangeOnFirstSelection={false}
                    />
                </PopoverBody>
            </Popover>
        </>
    )
}

export default DateRangeControl
